import { getStoredLanguageCode } from '../utils/language';

const BASE_URL =
  process.env.REACT_APP_REST_URL || 'http://localhost:8081/rest/v1';

function getToken(): string | null {
  try {
    return JSON.parse(localStorage.getItem('jwt-attendance') || 'null');
  } catch {
    return null;
  }
}

function getAcceptLanguage(): string {
  return getStoredLanguageCode();
}

export async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept-Language': getAcceptLanguage(),
    ...(options.headers as Record<string, string>),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
    cache: 'no-store',
  });

  const json = await response.json();

  if (!response.ok) {
    const err = new Error(json.error || 'Unknown error');
    (err as Error & { code?: string }).code = json.code;
    throw err;
  }

  return json.data as T;
}

/** Like request, but returns a Blob (for PDF/file downloads) */
export async function requestBlob(
  path: string,
  options: RequestInit = {}
): Promise<{ blob: Blob; headers: Headers }> {
  const token = getToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept-Language': getAcceptLanguage(),
    ...(options.headers as Record<string, string>),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return { blob: await response.blob(), headers: response.headers };
}

/** Like request, but returns the full JSON body (not just .data) */
export async function requestFull<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept-Language': getAcceptLanguage(),
    ...(options.headers as Record<string, string>),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
    cache: 'no-store',
  });

  const json = await response.json();

  if (!response.ok) {
    throw new Error(json.error || 'Unknown error');
  }

  return json as T;
}
